import { useRecoilValue } from 'recoil';

import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';

import { uploadAnnotations } from 'src/api/projects';
import useAlertSnackbar from 'src/hooks/useAlertSnackbar';
import { projectState } from 'src/states/project';
import { Modality } from 'src/types/api/data/image';

interface UploadAnnotationsButtonProps {
  projectId: string;
  modalityLabel: Modality;
}

const UploadAnnotationsButton = ({
  projectId,
  modalityLabel,
}: UploadAnnotationsButtonProps): JSX.Element | null => {
  const { openAlertSnackbar } = useAlertSnackbar();

  const project = useRecoilValue(
    projectState.project({ projectId, modality: modalityLabel })
  );

  const isCaseCurator = project.dataSource === 'CASE_CURATOR';
  const isConfirmed = project.confirmed;
  const isEnabled = isCaseCurator && isConfirmed;

  if (!isCaseCurator) return null;

  const handleUpload = async () => {
    try {
      const response = await uploadAnnotations(projectId, modalityLabel);
      openAlertSnackbar({
        severity: 'success',
        description: `Annotations upload started. Task ID: ${response.task_id}`,
      });
    } catch {
      openAlertSnackbar({
        severity: 'error',
        description: 'Failed to upload annotations.',
      });
    }
  };

  const button = (
    <Button
      color="primary"
      variant="contained"
      disabled={!isEnabled}
      onClick={isEnabled ? handleUpload : undefined}
      sx={{
        textTransform: 'uppercase',
        fontSize: '12px',
        fontWeight: 500,
        borderRadius: '8px',
        color: '#ffffff',
      }}
    >
      Upload Annotations
    </Button>
  );

  if (!isEnabled) {
    return (
      <Tooltip
        title="Annotations can only be uploaded for confirmed projects"
        placement="top"
      >
        <span style={{ display: 'inline-block' }}>{button}</span>
      </Tooltip>
    );
  }

  return button;
};

export default UploadAnnotationsButton;
